// config/firebase.ts
// Firebase configuration and initialization.
//
// Eager init: app + auth + db. Both contexts (AuthContext,
// TournamentContext) need them at module-load time, so deferring
// would just stall the first render.
//
// Lazy init: functions + storage. Each ships its own SDK module
// (~80-150 KB combined). They're only consumed by a handful of
// flows (Cloud Function callables in AuthContext / parent-child
// service / staff settings, and image upload on DQ case forms),
// none of which are hit on first paint. Consumers go through the
// helpers below (getCallable / dynamic import in dqCaseImageService)
// so the storage + functions SDK modules land in their own chunks
// and don't bloat main.js.

import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth } from 'firebase/auth';
import { getDatabase, Database } from 'firebase/database';

// Firebase configuration type
interface FirebaseConfig {
  apiKey: string | undefined;
  authDomain: string | undefined;
  databaseURL: string | undefined;
  projectId: string | undefined;
  storageBucket: string | undefined;
  messagingSenderId: string | undefined;
  appId: string | undefined;
}

const firebaseConfig: FirebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

export const app: FirebaseApp = initializeApp(firebaseConfig);

// Eagerly-initialised services — needed app-wide.
export const auth: Auth = getAuth(app);
export const db: Database = getDatabase(app);

export default app;
